import type { FocusEvent, KeyboardEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { cn } from '~/lib/cn';
import type { MediaPlanForm } from '~/modules/artist-roster/pages/artist-details/pages/media-planner/types';
import { DIRTY_VALIDATE_TOUCH } from '~/modules/artist-roster/pages/constants';

export function PlanName() {
  const { t } = useTranslation('campaign-planner');
  const { formState, setValue, register } = useFormContext<MediaPlanForm>();
  const [planName, setPlanName] = useState(
    formState.defaultValues?.planName || '',
  );
  const planNameOriginalRef = useRef('');
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const isEscapeBlurRef = useRef(false);

  useEffect(() => {
    return () => {
      clearTimeout(timer.current);
    };
  }, []);

  const handlePlanNameInputFocus = (event: FocusEvent<HTMLInputElement>) => {
    const input = event.currentTarget;

    planNameOriginalRef.current = input?.value || '';

    const textLength = input.value.length;

    // Move cursor to the end of the input value on focus.
    input.selectionStart = input.selectionEnd = textLength;

    timer.current = setTimeout(() => {
      input.selectionStart = input.selectionEnd = textLength;
      // If the input value is longer than the input field, horizontally scroll to the end.
      input.scrollLeft = input.scrollWidth;
    }, 0);
  };

  const handlePlanNameInputKeyDown = (
    event: KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.blur();
    }

    if (event.key === 'Escape') {
      event.preventDefault();

      setPlanName(planNameOriginalRef.current);

      isEscapeBlurRef.current = true;
      event.currentTarget.blur();
    }
  };

  const handlePlanNameInputBlur = (event: FocusEvent<HTMLInputElement>) => {
    // Skip blur handler if triggered by Escape key
    if (isEscapeBlurRef.current) {
      isEscapeBlurRef.current = false;

      return;
    }

    const input = event.currentTarget;

    // If the plan name is empty, revert to the original value.
    if (input.value.trim() === '') {
      setPlanName(planNameOriginalRef.current);

      return;
    }

    const inputValueTrimmed = input.value.trim().replace(/\s+/g, ' ');

    setPlanName(inputValueTrimmed);
    setValue('planName', inputValueTrimmed, DIRTY_VALIDATE_TOUCH);
  };

  return (
    <div className="mb-md flex flex-col items-start gap-xs">
      <label className="text-body-sm" htmlFor="plan-name">
        {t('digitalCampaigns.mediaPlan.header.planName')}
      </label>
      <div className="relative z-0 max-w-full">
        <input type="hidden" {...register('planName')} />
        <input
          type="text"
          name="planName"
          id="plan-name"
          className={cn(
            // The "peer" class is used to show/hide the pencil icon on hover/focus (see div element below).
            'peer relative z-10 h-2xl min-w-5xl max-w-full text-ellipsis px-sm pr-lg !leading-loose duration-300',
            'rounded-xs border outline-none bg-transparent border-transparent',
            'cursor-pointer text-display-sm font-bold decoration-1 underline-offset-8 text-default field-sizing-content',
            'hover:underline focus:cursor-text focus:no-underline focus:bg-emphasis-default focus:border-primary-default',
          )}
          value={planName}
          onChange={(event) => setPlanName(event.target.value)}
          maxLength={100}
          onFocus={handlePlanNameInputFocus}
          onBlur={handlePlanNameInputBlur}
          onKeyDown={handlePlanNameInputKeyDown}
        />
        <div
          className="absolute inset-y-0 right-0 grid place-items-center opacity-none duration-300 peer-hover:opacity-full peer-focus:opacity-none"
          aria-hidden="true"
        >
          <Icon
            type="pencil"
            variant="light"
            size="sm"
            className="!leading-none"
          />
        </div>
      </div>
    </div>
  );
}

PlanName.displayName = 'PlanName';
